import { forwardRef, useId } from "react";

import { FormHelperText } from "@mui/material";
import { XIcon } from "components/Icon";

import { useTextField } from "./TextField.hooks";
import {
  AddonWrapper,
  Container,
  Input,
  InputWrapper,
} from "./TextField.styles";
import { TextFieldProps, TextFieldReference } from "./TextField.types";
import InputLabel from "../InputLabel/InputLabel";

const TextField = forwardRef<TextFieldReference, TextFieldProps>(
  (
    {
      label,
      id,
      defaultValue,
      value,
      readOnly,
      fullWidth = true,
      InputProps = {},
      addonBefore,
      addonBeforeProps,
      addonAfter,
      addonAfterProps,
      helperText,
      tooltipText,
      clearable,
      onChangeEvent,
      onChange,
      ...innerProps
    },
    ref
  ) => {
    const { textfieldRef, valueState, onChangeHandler, onClearStateHandler } =
      useTextField({
        ref,
        defaultValue,
        value,
        onChangeEvent,
        onChange,
      });
    const formId = useId();

    return (
      <Container>
        {!!label && (
          <InputLabel
            htmlFor={id ?? formId}
            required={innerProps.required}
            helperText={tooltipText}
          >
            {label}
          </InputLabel>
        )}
        <InputWrapper fullWidth={fullWidth} color={innerProps.color}>
          {addonBefore ? (
            <AddonWrapper
              placement="before"
              disabled={innerProps.disabled}
              {...addonBeforeProps}
            >
              {addonBefore}
            </AddonWrapper>
          ) : null}
          <Input
            withAddonBefore={!!addonBefore}
            withAddonAfter={!!addonAfter}
            id={id ?? formId}
            variant="outlined"
            size="small"
            ref={textfieldRef}
            fullWidth={fullWidth}
            value={valueState}
            onChange={onChangeHandler}
            InputProps={{
              readOnly,
              endAdornment:
                clearable && valueState ? (
                  <XIcon
                    cursor="pointer"
                    onClick={onClearStateHandler}
                    sx={{
                      color: "#667085",
                      fontSize: "24px",
                      "& path": {
                        strokeWidth: "1.5",
                      },
                    }}
                  />
                ) : null,
              ...InputProps,
            }}
            {...innerProps}
          />
          {addonAfter ? (
            <AddonWrapper
              placement="after"
              disabled={innerProps.disabled}
              {...addonAfterProps}
            >
              {addonAfter}
            </AddonWrapper>
          ) : null}
        </InputWrapper>
        {helperText ? (
          <FormHelperText
            variant="outlined"
            required={innerProps.required}
            error={innerProps.error}
            disabled={innerProps.disabled}
          >
            {helperText}
          </FormHelperText>
        ) : null}
      </Container>
    );
  }
);

export default TextField;
